// ---------------------------------------------------------------------------
// DnD Email Editor – Saved Component Picker
//
// Opened by the single "Components" palette tile (drag-to-position or click).
// Deliberately mirrors FtpTemplatePicker's layout — list of thumbnails on the
// left, large preview on the right, Cancel / confirm in the footer — and reuses
// its ftp-modal / ftp-tmpl-* classes, which are already in the DnD editor's
// bundle because it mounts several other ftp-editor modals.
//
// Unlike the template picker this does NOT replace the email: it inserts the
// chosen component's layers at the drop position, so there is no confirm-to-
// overwrite step.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { SavedBlockEntry, SavedBlockRef } from './dnd-blocks';
import '@/components/ftp-editor/ftp-editor.css';

export interface DndComponentPickerProps {
  isOpen: boolean;
  /** The component entries only — header/footer are separate palette tiles. */
  components: SavedBlockEntry[];
  onClose: () => void;
  onInsert: (ref: SavedBlockRef) => void;
}

export function DndComponentPicker({
  isOpen,
  components,
  onClose,
  onInsert,
}: DndComponentPickerProps) {
  const [selectedRef, setSelectedRef] = useState<SavedBlockRef | null>(null);
  const [search, setSearch] = useState('');

  // Reset per open so a previous selection can't be inserted by accident.
  useEffect(() => {
    if (!isOpen) {
      setSelectedRef(null);
      setSearch('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return components;
    return components.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        (c.description ?? '').toLowerCase().includes(q),
    );
  }, [components, search]);

  const selected = useMemo(
    () => components.find((c) => c.ref === selectedRef) ?? null,
    [components, selectedRef],
  );

  if (!isOpen) return null;

  const handleInsert = () => {
    if (!selectedRef) return;
    onInsert(selectedRef);
    onClose();
  };

  return createPortal(
    <div
      className="ftp-modal-overlay active"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="ftp-modal ftp-template-picker"
        role="dialog"
        aria-labelledby="dnd-component-picker-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="ftp-modal-header">
          <h3 id="dnd-component-picker-title">Insert Component</h3>
          <button type="button" className="ftp-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="ftp-modal-body ftp-tmpl-body">
          <div className="ftp-tmpl-search-bar">
            <div className="ftp-tmpl-search-wrap">
              <i aria-hidden />
              <input
                type="search"
                placeholder="Search components…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoComplete="off"
                autoFocus
              />
            </div>
          </div>

          <div className="ftp-tmpl-layout">
            <div className="ftp-tmpl-list">
              {visible.length === 0 ? (
                <div className="ftp-tmpl-preview-empty">
                  <p>
                    {search
                      ? 'No components match that search'
                      : 'No saved components yet. Build one under Marketing → Templates → Email Components.'}
                  </p>
                </div>
              ) : (
                visible.map((c) => (
                  <div
                    key={c.ref}
                    role="button"
                    tabIndex={0}
                    className={`ftp-tmpl-card${selectedRef === c.ref ? ' selected' : ''}`}
                    onClick={() => setSelectedRef(c.ref)}
                    onDoubleClick={() => {
                      setSelectedRef(c.ref);
                      onInsert(c.ref);
                      onClose();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedRef(c.ref);
                      }
                    }}
                  >
                    <div className="ftp-tmpl-card-thumb">
                      <iframe title="" srcDoc={c.previewHtml || ''} />
                    </div>
                    <div className="ftp-tmpl-card-info">
                      <div className="ftp-tmpl-card-name">{c.label}</div>
                      {c.description ? (
                        <div className="ftp-tmpl-card-meta">
                          <span className="ftp-tmpl-card-date">{c.description}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="ftp-tmpl-preview-pane">
              {!selected ? (
                <div className="ftp-tmpl-preview-empty">
                  <i aria-hidden />
                  <p>Select a component to preview</p>
                </div>
              ) : selected.previewHtml ? (
                <iframe
                  className="ftp-tmpl-preview-frame"
                  title="Component preview"
                  srcDoc={selected.previewHtml}
                  style={{ display: 'block' }}
                />
              ) : (
                <div className="ftp-tmpl-preview-empty">
                  <p>
                    No preview available for this component — it will still insert
                    correctly.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="ftp-modal-footer">
          <span className="ftp-picker-info">
            The component is added at the spot you dropped it and stays editable.
          </span>
          <div className="ftp-modal-actions">
            <button type="button" className="ftp-btn ftp-btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="ftp-btn ftp-btn-primary"
              onClick={handleInsert}
              disabled={!selectedRef}
            >
              Add Component
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
